import React, { FC, useEffect, useState } from "react"
import { Button, Col, Checkbox, Divider, Empty, Input, Layout, Modal, Row, Space, Tabs, Tag, Table, Typography, Tooltip } from "antd"
import { pointSystem as testPointSystem } from "../models/test/SolosPointSystem"
import { PointSystem } from "../models/leaderboards/PointSystem"
import { PlacementCard } from "./leaderboards/PlacementCard"
import { columns } from "../models/leaderboards/LeaderboardColumns"
import { InfoCircleOutlined, TrophyOutlined } from "@ant-design/icons"
import { HeaderText } from "./common/HeaderText"

const { Header, Content } = Layout
const { TabPane } = Tabs
const { Text } = Typography
const { Search } = Input

export interface LeaderboardStatsProps {
    pointSystem?: any
    matches?: Array<any>
    total?: Array<any>
    hideHeader?: boolean
}

export const LeaderboardStats: FC<LeaderboardStatsProps> = (props) => {
    const [pointSystem, setPointSystem] = useState<PointSystem>(testPointSystem)
    const [columnSetting] = useState<Array<any>>(columns)
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
    const [nameFilter, setNameFilter] = useState<String>('')
    const [mapPointsVisible, setMapPointsVisible] = useState<boolean>(false)
    const [matchPointsVisible, setMatchPointsVisible] = useState<boolean>(false)

    useEffect(() => {
        if (props.pointSystem !== undefined) {
            setPointSystem(props.pointSystem)
        }
    }, [props.pointSystem])

    const handlePointSystemClick = () => {
        setIsModalVisible(true)
    }

    const handleModalCancel = () => {
        setIsModalVisible(false)
    }

    const handleModalOk = () => {
        setIsModalVisible(false)
    }

    const displayMatchPlacements = (matches: Array<any>) => {
        var bonusMatch = -1

        if (matches.length === 0) {
            return (
                <TabPane tab={"No matches found."} key={"N/A"}>
                    <Empty description={"No matches found."} />
                </TabPane>
            )
        }

        const matchCount = matches.length
        const tournamentPoints = new Map()

        const matchPoints = matches.map((match) => {
            const matchPlacement = JSON.parse(match.placement)

            const matchPlacementStats: Array<any> = []
            matchPlacement.forEach((team: any) => {
                var placementPoint = 0
                const placement: Array<any> = pointSystem.placements.filter(placement => { return placement.placement === team.rank })
                if (placement.length === 1) {
                    placementPoint = placement[0].points
                }
                const killPoints = (team.kills * pointSystem.kills)
                const totalPoints = killPoints + placementPoint
                matchPlacementStats.push({
                    key: team.players,
                    players: team.players.map((player: string) => { return <Tag key={match.matchid + player}>{player}</Tag> }),
                    placement: team.rank,
                    placementPoints: placementPoint,
                    kills: team.kills,
                    damage: team.damage,
                    killPoints: killPoints,
                    totalPoints: totalPoints
                })

                const teamPlayers = team.players.sort((a: any, b: any) => a.localeCompare(b)).join("|") // Order names alphabetically
                var exists = false
                if (tournamentPoints.size === 0) {
                    tournamentPoints.set(teamPlayers, [{
                        match: match.matchnumber,
                        points: totalPoints,
                        placementPoints: placementPoint,
                        kills: team.kills,
                        damage: team.damage,
                        map: match.mapname
                    }])
                    exists = true
                } else {
                    if (tournamentPoints.get(teamPlayers) === undefined) {
                        tournamentPoints.forEach((value: Array<any>, key: string) => {
                            const keySplit = key.split("|")
                            if (keySplit.length !== team.players.length) {
                                if (keySplit.length < team.players.length) { // So current match has more players
                                    if (team.players.includes(keySplit[0])) {
                                        console.debug("Current match has more players but matching player, so updating key to include everyone")
                                        const tempList = tournamentPoints.get(key)
                                        tempList.push({
                                            match: match.matchnumber,
                                            points: totalPoints,
                                            placementPoints: placementPoint,
                                            kills: team.kills,
                                            damage: team.damage,
                                            map: match.mapname
                                        })
                                        tournamentPoints.delete(key)
                                        tournamentPoints.set(teamPlayers, tempList)
                                        exists = true
                                    }
                                } else if (keySplit.length > team.players.length) { // So current match has less players
                                    if (keySplit.includes(team.players[0])) {
                                        console.debug("Current match has less players but matching player, so grouping score here")
                                        tournamentPoints.get(key).push({
                                            match: match.matchnumber,
                                            points: totalPoints,
                                            placementPoints: placementPoint,
                                            kills: team.kills,
                                            damage: team.damage,
                                            map: match.mapname
                                        })
                                        exists = true
                                    }
                                }

                            }
                        })
                    } else {
                        tournamentPoints.get(teamPlayers).push({
                            match: match.matchnumber,
                            points: totalPoints,
                            placementPoints: placementPoint,
                            kills: team.kills,
                            damage: team.damage,
                            map: match.mapname
                        })
                        exists = true
                    }
                }

                if (!exists) {
                    tournamentPoints.set(teamPlayers, [{
                        match: match.matchnumber,
                        points: totalPoints,
                        placementPoints: placementPoint,
                        kills: team.kills,
                        damage: team.damage,
                        map: match.mapname
                    }])
                }
            })

            if (match.matchname === "Bonus") {
                bonusMatch = match.matchnumber
            }

            const newSettings: Array<any> = []
            columnSetting.forEach((column: any) => {
                if (column.key === "players") {
                    newSettings.push({
                        ...column,
                        filteredValue: (nameFilter !== "") ? [nameFilter] : [],
                        onFilter: (value: string, record: any) => {
                            var nameMatch: Boolean = false
                            record.players.forEach((player: any) => {
                                if (player.props.children.toLowerCase().includes(value.toLowerCase())) {
                                    nameMatch = true
                                }
                            })
                            return nameMatch
                        },
                    })
                } else {
                    newSettings.push(column)
                }
            })

            return (
                <TabPane tab={`#${match.matchnumber} - ${match.matchname} [${match.mapname}] `} key={"leaderboard-" + match.matchid + "-table"}>
                    <Table columns={newSettings} dataSource={matchPlacementStats} key={match.matchid + "-table"} pagination={{ pageSize: 100 }} scroll={{ y: 800 }} />
                </TabPane>
            )
        })

        const totalPointsDataSource: Array<any> = []
        var activeMapList: Array<string> = []
        tournamentPoints.forEach((value: Array<any>, key: string) => {
            const tempMap = new Map()
            const mapPoints = new Map()
            var tempTotalPoints = 0
            var tempKills = 0
            var tempDamage = 0
            var tempPlacementPoints = 0
            tempMap.set("key", key.split("|"))
            tempMap.set("players", key.split("|"))
            for (var i = 1; i <= matchCount; i++) {
                const matchNumber = i
                const match = value.find((match) => { return match.match === matchNumber })
                if (match === undefined) {
                    tempMap.set(`match${i}`, 0)
                } else {
                    if (match.match === bonusMatch) {
                        tempMap.set("bonus", match.points)
                    } else {
                        tempMap.set(`match${match.match}`, match.points)
                        tempTotalPoints += match.points
                        tempKills += match.kills
                        tempDamage += parseFloat(match.damage)
                        tempPlacementPoints += match.placementPoints

                        if (mapPoints.get(match.map) === undefined) {
                            mapPoints.set(match.map, match.points)
                        } else {
                            mapPoints.set(match.map, mapPoints.get(match.map) + match.points)
                        }
                    }
                }
            }
            tempMap.set("damage", tempDamage)
            tempMap.set("total", tempTotalPoints)
            tempMap.set("kills", tempKills)
            tempMap.set("placementPoints", tempPlacementPoints)
            mapPoints.forEach((value: boolean, key: string) => {
                tempMap.set(key, value)
            })
            if (activeMapList.length === 0) {
                activeMapList = Object.keys(Object.fromEntries(mapPoints))
            }
            totalPointsDataSource.push(Object.fromEntries(tempMap))
        })

        const pointSortedTotalPoints = totalPointsDataSource.sort((a, b) => { return b.total - a.total })
        const killSortedTotalPoints = pointSortedTotalPoints.sort(
            (a, b) => {
                if (a.total === b.total) {
                    return b.kills - a.kills
                }
                return 0
            }
        )
        const sortedTotalPoints = killSortedTotalPoints.sort(
            (a, b) => {
                if (a.total === b.total && a.kills === b.kills) {
                    return b.damage - a.damage
                }
                return 0
            }
        )

        const leaderBoardTotalColumns: Array<any> = [
            {
                title: "#",
                key: "index",
                width: "64px",
                render: (text: any, record: any) => {
                    const placement = sortedTotalPoints.indexOf(record) + 1
                    switch (placement) {
                        case 1:
                            return ({
                                props: {
                                    style: {
                                        background: "#d89614",
                                        color: "black"
                                    }
                                },
                                children: (<>
                                    <TrophyOutlined /> {placement}
                                </>)
                            })
                        case 2:
                            return ({
                                props: {
                                    style: {
                                        background: "silver",
                                        color: "black"
                                    }
                                },
                                children: (<>
                                    <TrophyOutlined /> {placement}
                                </>)
                            })
                        case 3:
                            return ({
                                props: {
                                    style: {
                                        background: "#443111",
                                        color: "black"
                                    }
                                },
                                children: (<>
                                    <TrophyOutlined /> {placement}
                                </>)
                            })
                        default:
                            return placement
                    }
                }
            },
            {
                title: "Players",
                dataIndex: "players",
                key: "players",
                filteredValue: (nameFilter !== "") ? [nameFilter] : [],
                onFilter: (value: string, record: any) => {
                    var nameMatch: Boolean = false
                    record.players.forEach((player: string) => {
                        if (player.toLowerCase().includes(value.toLowerCase())) {
                            nameMatch = true
                        }
                    })
                    return nameMatch
                },
                render: (players: Array<any>) => players.map((player) => { return <Tag key={"total-" + player}>{player}</Tag> })
            }
        ]

        if (matchPointsVisible) {
            for (var i = 1; i <= matchCount; i++) {
                if (i !== bonusMatch) {
                    const matchNumber = i
                    leaderBoardTotalColumns.push({
                        title: `#${i}`,
                        dataIndex: "match" + i.toString(),
                        key: `match${i}`,
                        sorter: {
                            compare: (a: any, b: any) => {
                                return a["match" + matchNumber] - b["match" + matchNumber]
                            },
                        },
                        sortDirections: ['descend', 'descend'],
                    })
                }
            }
        }

        if (bonusMatch !== -1) {
            leaderBoardTotalColumns.push({
                title: "Bonus",
                dataIndex: "bonus",
                key: "bonus",
                sorter: {
                    compare: (a: any, b: any) => a.bonus - b.bonus,
                },
                sortDirections: ['descend', 'descend']
            })
        }

        if (mapPointsVisible) {
            activeMapList.forEach((map) => {
                leaderBoardTotalColumns.push({
                    title: map,
                    dataIndex: map,
                    key: map,
                    sorter: {
                        compare: (a: any, b: any) => a[map] - b[map],
                    },
                    sortDirections: ['descend', 'descend'],
                    render: (a: number) => a === undefined ? 0 : a
                })
            })
        }

        leaderBoardTotalColumns.push({
            title: "Placement Points",
            dataIndex: "placementPoints",
            key: "placementPoints",
            sorter: {
                compare: (a: any, b: any) => a.placementPoints - b.placementPoints,
            },
            sortDirections: ['descend', 'descend'],
        })

        leaderBoardTotalColumns.push({
            title: "Kills",
            dataIndex: "kills",
            key: "kills",
            sorter: {
                compare: (a: any, b: any) => a.kills - b.kills,
            },
            sortDirections: ['descend', 'descend'],
        })

        leaderBoardTotalColumns.push({
            title: "Damage",
            dataIndex: "damage",
            key: "damage",
            sorter: {
                compare: (a: any, b: any) => a.damage - b.damage,
            },
            sortDirections: ['descend', 'descend'],
            render: (damage: number) => damage.toFixed(2)
        })

        leaderBoardTotalColumns.push({
            title: "Total",
            dataIndex: "total",
            key: "total",
            sorter: {
                compare: (a: any, b: any) => a.total - b.total,
            },
            sortDirections: ['descend', 'descend'],
            defaultSortOrder: "descend"
        })

        // console.log(sortedTotalPoints[0]["match" + 1])

        matchPoints.unshift(
            <TabPane tab={"Total"} key={"leaderboard-total"}>
                <Table columns={leaderBoardTotalColumns} dataSource={sortedTotalPoints} pagination={{ pageSize: 100 }} scroll={{ y: 800, x: "auto" }} key={"leaderboard-total-table"} />
            </TabPane>
        )

        return matchPoints
    }

    return (
        <>
            <Layout>
                {!props?.hideHeader &&
                    <>
                        <Header>
                            <Row justify="space-between" align="middle" >
                                <HeaderText title={"Leaderboards"} />
                                <Tooltip placement="topLeft" title="Point System" trigger={["hover", "click"]}>
                                    <Space>
                                        <Button icon={<InfoCircleOutlined />} onClick={handlePointSystemClick} >
                                            <Space className="hiddenOnMobile" style={{ paddingLeft: "4px" }}>
                                                Point System
                                            </Space>
                                        </Button>
                                    </Space>
                                </Tooltip>
                            </Row>
                        </Header>
                        <Divider />
                    </>
                }
                <Content>
                    <Space direction="horizontal" size="large" align="center" style={{ paddingTop: '4px', paddingBottom: '4px', width: "100%" }}>
                        <Search placeholder="Search Player" onSearch={setNameFilter} type="primary" />
                        <Button onClick={(e) => setNameFilter('')}>Clear Search</Button>
                    </Space>
                    <Space direction="horizontal" size="large" align="center" style={{ paddingTop: '4px', paddingBottom: '4px', width: "100%" }}>
                        <Checkbox onChange={(e) => setMatchPointsVisible(e.target.checked)}>Points by Match</Checkbox>
                        <Checkbox onChange={(e) => setMapPointsVisible(e.target.checked)}>Points by Map</Checkbox>
                    </Space>
                    <Tabs>
                        {displayMatchPlacements(props.matches || [])}
                    </Tabs>
                </Content>
            </Layout>
            <Modal
                title={"Point System"}
                width={"calc(100vw - 20%)"}
                visible={isModalVisible}
                onCancel={handleModalCancel}
                onOk={handleModalOk}
            >
                <Space direction="vertical" size="large">
                    <Text style={{ fontSize: 24 }}>Kills are worth {pointSystem?.kills} points each.</Text>
                    <Row justify="space-around">
                        <Space direction="horizontal" size="middle" wrap={true}>
                            {pointSystem.placements.map((placement) => {
                                return (
                                    <Col span={3} key={placement.placement}>
                                        <PlacementCard placement={placement.placement} points={placement.points} key={placement.placement} />
                                    </Col>
                                )
                            })}
                        </Space>
                    </Row>
                </Space>
            </Modal>
        </>
    )
}